import { getSegmentCarePartnerScrubMiddlewareSnippet } from '@/lib/analytics/carePartnerTelemetryScrub'
import { resolveTenantHeaderId } from '@/lib/tenantHeaders'

/** Deploy tenant slugs are lowercase kebab ids (e.g. general-medicine, walmart). */
const SEGMENT_TENANT_SLUG_PATTERN = /^[a-z0-9-]+$/

/** Deploy tenant slug for Segment, matching Nile-Core `X-GM-Tenant`. */
export function getSegmentTenantSlug(): string {
    return resolveTenantHeaderId()
}

/**
 * Encode a deploy tenant slug as a JS string literal for the Segment snippet.
 * Rejects values outside the known slug charset before embedding into code.
 */
export function encodeSegmentTenantSlugLiteral(tenantSlug: string): string {
    if (!SEGMENT_TENANT_SLUG_PATTERN.test(tenantSlug)) {
        throw new Error(`Invalid Segment tenant slug: ${tenantSlug}`)
    }
    return JSON.stringify(tenantSlug)
}

/**
 * Registers Segment source middleware that stamps deploy tenant_slug on
 * track/page properties and identify traits. Must run after the Segment stub
 * is created and before `analytics.load()`, so the first page is covered.
 */
export function getSegmentTenantMiddlewareSnippet(tenantSlug: string): string {
    const tenantSlugLiteral = encodeSegmentTenantSlugLiteral(tenantSlug)
    return `var tenantSlug=${tenantSlugLiteral};analytics.addSourceMiddleware(function(chain){var payload=chain.payload;var next=chain.next;var type=payload.obj&&payload.obj.type;if(type==='track'||type==='page'){payload.obj.properties=Object.assign({},payload.obj.properties||{},{tenant_slug:tenantSlug})}else if(type==='identify'){payload.obj.traits=Object.assign({},payload.obj.traits||{},{tenant_slug:tenantSlug})}next(payload)});`
}

/**
 * Inserts Segment source middleware immediately before `analytics.load()` inside a browser snippet:
 * tenant_slug stamping, then care-partner link-token redaction for page `u`/`p` context and properties.
 */
export function injectSegmentTenantMiddleware(segmentSnippet: string, tenantSlug: string): string {
    const middleware =
        getSegmentTenantMiddlewareSnippet(tenantSlug) + getSegmentCarePartnerScrubMiddlewareSnippet()
    const loadCall = 'analytics.load('
    const loadIndex = segmentSnippet.indexOf(loadCall)
    if (loadIndex === -1) {
        throw new Error('Segment snippet is missing analytics.load(); cannot register tenant middleware')
    }
    return `${segmentSnippet.slice(0, loadIndex)}${middleware}${segmentSnippet.slice(loadIndex)}`
}
